import { type CSSProperties } from 'react';
import { IconButton, Icon, Tooltip, Select, Text, icon } from '@nula/core-ui';
import type { FormattingState, FontFamily } from '@/features/canvas/engine/hooks/use-formatting-state';
import styles from './font-group.module.css';

const FONT_SIZES = [10, 12, 14, 16, 18, 24, 32, 48];

const DEFAULT_FONT_FAMILY = 'Instrument Sans, sans-serif';
const FONT_ALIAS: Record<string, string> = { sans: DEFAULT_FONT_FAMILY };

const FONT_OPTIONS: { value: string; label: string }[] = [
  { value: 'Arial, sans-serif',               label: 'Arial' },
  { value: 'DM Sans, sans-serif',             label: 'DM Sans' },
  { value: 'Instrument Sans, sans-serif',     label: 'Instrument Sans' },
  { value: 'Inter, sans-serif',               label: 'Inter' },
  { value: 'Lato, sans-serif',                label: 'Lato' },
  { value: 'Noto Sans, sans-serif',           label: 'Noto Sans' },
  { value: 'Nunito, sans-serif',              label: 'Nunito' },
  { value: 'Open Sans, sans-serif',           label: 'Open Sans' },
  { value: 'Plus Jakarta Sans, sans-serif',   label: 'Plus Jakarta Sans' },
  { value: 'Raleway, sans-serif',             label: 'Raleway' },
  { value: 'Roboto, sans-serif',              label: 'Roboto' },
  { value: 'Roboto Condensed, sans-serif',    label: 'Roboto Condensed' },
  { value: 'EB Garamond, serif',              label: 'EB Garamond' },
  { value: 'Georgia, serif',                  label: 'Georgia' },
  { value: 'Libre Baskerville, serif',        label: 'Libre Baskerville' },
  { value: 'Lora, serif',                     label: 'Lora' },
  { value: 'Merriweather, serif',             label: 'Merriweather' },
  { value: 'Playfair Display, serif',         label: 'Playfair Display' },
  { value: 'Roboto Slab, serif',              label: 'Roboto Slab' },
  { value: 'Times New Roman, serif',          label: 'Times New Roman' },
  { value: 'Courier New, monospace',          label: 'Courier New' },
  { value: 'Fira Code, monospace',            label: 'Fira Code' },
  { value: 'JetBrains Mono, monospace',       label: 'JetBrains Mono' },
  { value: 'Roboto Mono, monospace',          label: 'Roboto Mono' },
  { value: 'Caveat, cursive',                 label: 'Caveat' },
  { value: 'Dancing Script, cursive',         label: 'Dancing Script' },
  { value: 'Pacifico, cursive',               label: 'Pacifico' },
  { value: 'Permanent Marker, cursive',       label: 'Permanent Marker' },
];

const FONT_SELECT_OPTIONS = FONT_OPTIONS.map(({ value, label }) => ({
  value,
  label: <span style={{ '--font-preview': value } as CSSProperties} className={styles.fontOption}>{label}</span>,
}));

/**
 * Reusable font-family + size cluster for any node type that has formatting
 * controls (Sticky, Shape, Text, Frame). Reads/writes via the shared
 * useFormattingState hook so adding a new consumer is "just import + state".
 */
export function FontGroup({ state }: { state: FormattingState }) {
  const sizeIdx = FONT_SIZES.indexOf(state.fontSize);

  return (
    <>
      <Select
        value={FONT_ALIAS[state.fontFamily] ?? state.fontFamily ?? DEFAULT_FONT_FAMILY}
        options={FONT_SELECT_OPTIONS}
        onChange={(v) => state.setFontFamily(v as FontFamily)}
        size="sm"
        variant="default"
        aria-label="Font"
        className={styles.fontSelect}
        popupMatchSelectWidth={false}
      />
      <Tooltip title="Decrease font size" placement="top">
        <IconButton
          transparent
          aria-label="Decrease font size"
          className={styles.btn}
          onClick={() => { if (sizeIdx > 0) state.setFontSize(FONT_SIZES[sizeIdx - 1]); }}
        >
          <Icon name="remove" size={icon.md} />
        </IconButton>
      </Tooltip>
      <Text size="sm" className={styles.fontSizeLabel}>{state.fontSize}</Text>
      <Tooltip title="Increase font size" placement="top">
        <IconButton
          transparent
          aria-label="Increase font size"
          className={styles.btn}
          onClick={() => { if (sizeIdx < FONT_SIZES.length - 1) state.setFontSize(FONT_SIZES[sizeIdx + 1]); }}
        >
          <Icon name="add" size={icon.md} />
        </IconButton>
      </Tooltip>
    </>
  );
}
